import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Button, Card, Input, Switch, Textarea } from '@rallyshift/design-system'
import api from '../../api/client'
import type { EventInput } from '../../types'

/** yyyy-MM-dd and HH:mm in the browser's zone, which is the zone organisers work in. */
function splitLocal(iso: string): { date: string; time: string } {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  }
}

function toIso(date: string, time: string): string {
  return new Date(`${date}T${time}`).toISOString()
}

/**
 * Create and edit the events an organisation runs. Shifts hang off these, so this is
 * the first thing an organiser needs and until now the only way to get one was to
 * insert a row by hand.
 */
const EventBuilderPage: React.FC = () => {
  const navigate = useNavigate()
  const { eventId } = useParams<{ eventId: string }>()
  const editing = Boolean(eventId)

  const today = splitLocal(new Date().toISOString()).date

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [startDate, setStartDate] = useState(today)
  const [startTime, setStartTime] = useState('09:00')
  const [endDate, setEndDate] = useState(today)
  const [endTime, setEndTime] = useState('17:00')
  const [location, setLocation] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [price, setPrice] = useState('')
  const [maxCapacity, setMaxCapacity] = useState(100)
  const [requiresTicket, setRequiresTicket] = useState(true)
  const [hasLiveStream, setHasLiveStream] = useState(false)
  const [liveStreamUrl, setLiveStreamUrl] = useState('')
  const [hasRadioStream, setHasRadioStream] = useState(false)
  const [radioStreamUrl, setRadioStreamUrl] = useState('')

  const [loading, setLoading] = useState(editing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!eventId) return
    api.events
      .get(eventId)
      .then((event) => {
        const start = splitLocal(event.start_date)
        const end = splitLocal(event.end_date)
        setTitle(event.title)
        setDescription(event.description ?? '')
        setStartDate(start.date)
        setStartTime(start.time)
        setEndDate(end.date)
        setEndTime(end.time)
        setLocation(event.location ?? '')
        setImageUrl(event.image_url ?? '')
        setPrice(event.price === null || event.price === undefined ? '' : String(event.price))
        setMaxCapacity(event.max_capacity ?? 100)
        setRequiresTicket(event.requires_ticket ?? true)
        setHasLiveStream(event.has_live_stream ?? false)
        setLiveStreamUrl(event.live_stream_url ?? '')
        setHasRadioStream(event.has_radio_stream ?? false)
        setRadioStreamUrl(event.radio_stream_url ?? '')
      })
      .catch((e) => setError(e.message || 'Dit evenement kon niet worden geladen.'))
      .finally(() => setLoading(false))
  }, [eventId])

  const startsAt = `${startDate}T${startTime}`
  const endsAt = `${endDate}T${endTime}`
  const ordered = Boolean(startDate && endDate && endsAt >= startsAt)
  const valid = title.trim() && location.trim() && ordered

  const submit = async () => {
    if (!valid) {
      setError(
        ordered
          ? 'Vul een titel en een locatie in.'
          : 'Het evenement kan niet eindigen voordat het begint.',
      )
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload: EventInput = {
        title: title.trim(),
        description: description.trim(),
        start_date: toIso(startDate, startTime),
        end_date: toIso(endDate, endTime),
        location: location.trim(),
        image_url: imageUrl.trim() || null,
        price: price.trim() === '' ? null : Number(price),
        max_capacity: maxCapacity,
        requires_ticket: requiresTicket,
        has_live_stream: hasLiveStream,
        // A stream that is switched off keeps no address, so turning it back on does
        // not quietly republish a link the organiser had forgotten about.
        live_stream_url: hasLiveStream ? liveStreamUrl.trim() || null : null,
        has_radio_stream: hasRadioStream,
        radio_stream_url: hasRadioStream ? radioStreamUrl.trim() || null : null,
      }

      if (eventId) {
        await api.events.update(eventId, payload)
      } else {
        await api.events.create(payload)
      }
      navigate('/events')
    } catch (e: any) {
      setError(e.message || 'Opslaan is niet gelukt.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-rs-spin rounded-full border-2.5 border-action-primary border-r-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h1 className="font-display text-display-lg text-text-primary">
        {editing ? 'Evenement bewerken' : 'Nieuw evenement'}
      </h1>

      {error && (
        <Alert variant="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <div className="space-y-5">
          <Input
            label="Titel"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Winterfeest 2026"
          />

          <Textarea
            label="Omschrijving"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Waar gaat dit evenement over?"
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Begindatum"
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value)
                // A one-day event is the common case, so the end follows the start
                // until the organiser pulls it apart themselves.
                if (endDate < e.target.value) setEndDate(e.target.value)
              }}
            />
            <Input
              label="Begintijd"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
            <Input
              label="Einddatum"
              type="date"
              min={startDate}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <Input
              label="Eindtijd"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>

          {!ordered && (
            <Alert variant="warning">Het evenement eindigt voordat het begint.</Alert>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Locatie"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Marktplein"
            />
            <Input
              label="Maximum aantal bezoekers"
              type="number"
              min={0}
              value={maxCapacity}
              onChange={(e) => setMaxCapacity(Math.max(0, Number(e.target.value) || 0))}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Prijs"
              type="number"
              min={0}
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              hint="Leeg laten als het evenement gratis is"
            />
            <Input
              label="Afbeelding (URL)"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://…"
            />
          </div>

          <div className="space-y-3 border-t border-border pt-5">
            <Switch
              label="Bezoekers hebben een ticket nodig"
              checked={requiresTicket}
              onChange={setRequiresTicket}
            />

            <Switch
              label="Livestream"
              description="Toon een videostream op de evenementpagina"
              checked={hasLiveStream}
              onChange={setHasLiveStream}
            />
            {hasLiveStream && (
              <Input
                label="Livestream-URL"
                value={liveStreamUrl}
                onChange={(e) => setLiveStreamUrl(e.target.value)}
                placeholder="https://…"
              />
            )}

            <Switch
              label="Radiostream"
              description="Toon een audiostream op de evenementpagina"
              checked={hasRadioStream}
              onChange={setHasRadioStream}
            />
            {hasRadioStream && (
              <Input
                label="Radiostream-URL"
                value={radioStreamUrl}
                onChange={(e) => setRadioStreamUrl(e.target.value)}
                placeholder="https://…"
              />
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={() => navigate('/events')}>
            Annuleren
          </Button>
          <Button onClick={submit} loading={saving}>
            {editing ? 'Wijzigingen opslaan' : 'Evenement aanmaken'}
          </Button>
        </div>
      </Card>
    </div>
  )
}

export default EventBuilderPage
